<template>
  <!-- Teleport 到 body，避免被 MainLayout sidebar（z-index: 200）蓋住 -->
  <Teleport to="body">
  <div class="modal-overlay" @click.self="handleClose">
    <div class="modal-content">
      <div class="modal-header">
        <h2>客戶訂閱與扣款詳情</h2>
        <button class="btn-close" @click="handleClose">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div v-if="loading" class="modal-body loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="modal-body error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="loadData">重試</button>
      </div>

      <div v-else class="modal-body">
        <!-- 客戶基本資訊 -->
        <div class="info-section">
          <h3>客戶基本資訊</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">客戶代碼：</span>
              <span class="info-value">{{ clientData?.client?.client_code || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">客戶名稱：</span>
              <span class="info-value">{{ clientData?.client?.client_name || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">平台：</span>
              <span class="info-value">{{ clientData?.client?.platform_name || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">方案：</span>
              <span class="info-value">{{ clientData?.client?.plan_name || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- 當前訂閱狀態（含已過期但可延長補繳時，仍顯示最後一筆訂閱列） -->
        <div v-if="subscriptionForDisplay" class="info-section">
          <h3>當前訂閱狀態</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">訂閱狀態：</span>
              <span :class="getSubscriptionStatusClass(clientData?.client?.subscription_status)">
                {{ getSubscriptionStatusText(clientData?.client?.subscription_status) }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">開始日期：</span>
              <span class="info-value">{{ formatDate(subscriptionForDisplay?.start_date) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">到期日期：</span>
              <span class="info-value">{{ formatDate(subscriptionForDisplay?.end_date) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">付款方式：</span>
              <span class="info-value">{{ subscriptionForDisplay?.payment_type === 'manual' ? '人工收款' : '綠界扣款' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">是否試用：</span>
              <span class="info-value">{{ subscriptionForDisplay?.is_trial ? '是' : '否' }}</span>
            </div>
            <div
              v-if="subscriptionForDisplay && !subscriptionForDisplay.is_trial"
              class="info-item"
            >
              <span class="info-label">自動續約：</span>
              <span class="info-value">{{ subscriptionForDisplay.auto_renew === false ? '已關閉' : '開啟' }}</span>
            </div>
          </div>
        </div>

        <!-- 訂閱操作區塊 -->
        <div v-if="clientData?.client" class="info-section action-section">
          <div class="section-header">
            <h3>訂閱操作</h3>
            <div class="action-buttons-group">
              <!-- 試用中：顯示「升級為正式訂閱」 -->
              <button
                v-if="isOnTrial"
                class="btn btn-success"
                @click="showUpgradeModal = true"
              >
                <i class="ri-vip-crown-line"></i>
                升級為正式訂閱
              </button>
              <!-- 有活躍訂閱（含試用）：顯示「延長訂閱」 -->
              <button 
                class="btn btn-primary" 
                @click="handleExtendButtonClick"
                :disabled="!canExtendSubscription"
              >
                <i class="ri-calendar-add-line"></i>
                延長訂閱
              </button>
              <button
                v-if="canCancelSubscriptionRenewal"
                type="button"
                class="btn btn-outline-secondary"
                :disabled="cancellingRenewal"
                @click="handleCancelSubscriptionRenewal"
              >
                <i class="ri-close-circle-line"></i>
                {{ cancellingRenewal ? '處理中...' : '取消自動續約' }}
              </button>
              <button
                v-if="pendingManualUpgrade"
                type="button"
                class="btn btn-warning"
                @click="openConfirmPendingModal"
              >
                <i class="ri-hand-coin-line"></i>
                確認升級收款（人工）
              </button>
            </div>
          </div>
          <div v-if="isOnTrial" class="info-note info-note-trial">
            <i class="ri-information-line"></i>
            <span>客戶目前為試用中。「升級為正式訂閱」會先建立待開通付費訂閱；試用會保留至綠界付款成功或您確認人工收款後才結束。</span>
          </div>
          <div v-if="pendingEcpayUpgrade" class="info-note info-note-pending">
            <i class="ri-bank-card-line"></i>
            <span>已發起綠界升級訂單（{{ pendingEcpayUpgrade.plan_name || '付費方案' }}），待客戶完成付款後試用將自動結束並開通正式訂閱。</span>
          </div>
          <div v-if="pendingManualUpgrade" class="info-note info-note-pending">
            <i class="ri-hand-coin-line"></i>
            <span>有待收款的正式訂閱（{{ pendingManualUpgrade.plan_name || '付費方案' }}），客戶付款後請點「確認升級收款（人工）」。</span>
          </div>
          <div v-if="!canExtendSubscription" class="info-note">
            <i class="ri-information-line"></i>
            <span>此客戶沒有可延長的訂閱紀錄（無 active／expired 訂閱列可供補繳延長）。請先建立新訂閱。</span>
          </div>
          <div v-if="!canExtendSubscription" class="action-buttons" style="margin-top: 12px;">
            <button 
              class="btn btn-success" 
              @click="showCreateSubscriptionModal = true"
            >
              <i class="ri-add-circle-line"></i>
              建立新訂閱
            </button>
          </div>
        </div>

        <!-- 扣款統計 -->
        <div v-if="clientData?.paymentStatistics" class="info-section">
          <h3>扣款統計</h3>
          <div class="stats-grid">
            <div class="stat-card success">
              <div class="stat-label">成功扣款</div>
              <div class="stat-value">
                {{ clientData.paymentStatistics.successful_payment_count || 0 }} 次
              </div>
              <div class="stat-amount">
                NT$ {{ formatCurrency(clientData.paymentStatistics.successful_total_amount || 0) }}
              </div>
              <div v-if="clientData.paymentStatistics.last_successful_payment_date" class="stat-date">
                最後成功：{{ formatDate(clientData.paymentStatistics.last_successful_payment_date) }}
              </div>
            </div>
            <div class="stat-card failed">
              <div class="stat-label">失敗扣款</div>
              <div class="stat-value">
                {{ clientData.paymentStatistics.failed_payment_count || 0 }} 次
              </div>
              <div v-if="clientData.paymentStatistics.last_failed_payment_date" class="stat-date">
                最後失敗：{{ formatDate(clientData.paymentStatistics.last_failed_payment_date) }}
              </div>
            </div>
          </div>
        </div>

        <!-- 訂閱歷史 -->
        <div class="info-section">
          <h3>訂閱歷史</h3>
          <p v-if="clientData?.subscriptionHistory?.length" class="table-scroll-hint">欄位較多時請左右滑動或拖曳捲軸，即可看到「綠界訂單編號」。</p>
          <div v-if="!clientData?.subscriptionHistory || clientData.subscriptionHistory.length === 0" class="empty-state">
            沒有訂閱歷史記錄
          </div>
          <div v-else class="detail-table-scroll">
            <table class="detail-table detail-table--subscription-history">
              <thead>
                <tr>
                  <th>方案</th>
                  <th>開始日期</th>
                  <th>結束日期</th>
                  <th>狀態</th>
                  <th>付款方式</th>
                  <th title="MerchantTradeNo，建立綠界訂單時寫入">綠界訂單編號</th>
                  <th>是否試用</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(sub, index) in clientData.subscriptionHistory" :key="index">
                  <td>{{ sub.plan_name || '-' }}</td>
                  <td>{{ formatDate(sub.start_date) }}</td>
                  <td>{{ formatDate(sub.end_date) }}</td>
                  <td>
                    <span :class="getSubscriptionRecordStatusClass(sub.status)">
                      {{ getSubscriptionRecordStatusText(sub.status) }}
                    </span>
                  </td>
                  <td>{{ sub.payment_type === 'manual' ? '人工收款' : '綠界扣款' }}</td>
                  <td>
                    <code v-if="getSubscriptionMerchantTradeNo(sub)" class="merchant-trade-no">{{ getSubscriptionMerchantTradeNo(sub) }}</code>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>{{ sub.is_trial ? '是' : '否' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 扣款紀錄 -->
        <div class="info-section">
          <h3>扣款紀錄</h3>
          <div v-if="!clientData?.paymentRecords || clientData.paymentRecords.length === 0" class="empty-state">
            沒有扣款記錄
          </div>
          <table v-else class="detail-table">
            <thead>
              <tr>
                <th>扣款日期</th>
                <th>金額</th>
                <th>付款方式</th>
                <th>狀態</th>
                <th>延長天數</th>
                <th>備註</th>
                <th class="col-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(record, index) in clientData.paymentRecords" 
                :key="index"
                :class="getPaymentRecordRowClass(record.status)"
              >
                <td>{{ formatDate(record.payment_date) }}</td>
                <td>
                  <template v-if="record.status === 'cancel_renewal'">—</template>
                  <template v-else>NT$ {{ formatCurrency(record.amount) }}</template>
                </td>
                <td>{{ record.payment_type === 'manual' ? '人工收款' : '綠界扣款' }}</td>
                <td>
                  <span :class="getPaymentStatusClass(record.status)">
                    {{ getPaymentStatusText(record.status) }}
                  </span>
                </td>
                <td>{{ record.extended_days || '-' }} 天</td>
                <td>
                  <span v-if="record.notes" :title="record.notes">{{ record.notes }}</span>
                  <span v-else-if="record.status === 'failed' && record.ecpay_response" class="text-danger">
                    失敗原因：{{ extractFailureReason(record.ecpay_response) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="col-actions">
                  <button
                    v-if="record.refundable"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="refundSubmitting"
                    @click="handleRefundClick(record)"
                  >
                    全額退款
                  </button>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="handleClose">關閉</button>
      </div>
    </div>

    <!-- 全額退款 Modal -->
    <div v-if="showRefundModal" class="modal-overlay" @click.self="closeRefundModal">
      <div class="extend-modal-content">
        <div class="modal-header">
          <h2>全額退款</h2>
          <button class="btn-close" type="button" @click="closeRefundModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="text-muted small">
            將依綠界退回該筆金額（人工收款僅更新紀錄）、調整訂閱到期日、關閉自動續訂並終止綠界定期定額；
            若該筆已開立電子發票，將一併呼叫綠界折讓（不發信通知，AllowanceNotify=N）。
          </p>
          <div v-if="refundTargetRecord" class="form-group">
            <label class="form-label">退款對象</label>
            <div class="text-body">
              {{ formatDate(refundTargetRecord.payment_date) }} · NT$ {{ formatCurrency(refundTargetRecord.amount) }}
              · {{ refundTargetRecord.payment_type === 'manual' ? '人工收款' : '綠界扣款' }}
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">備註（選填）</label>
            <textarea v-model="refundForm.operatorNote" class="form-control" rows="2" placeholder="例：客戶來電同意退款" />
          </div>
          <div v-if="refundTargetRecord?.payment_type === 'ecpay'" class="form-group">
            <label class="checkbox-label">
              <input v-model="refundForm.refundWithoutEcpay" type="checkbox" />
              略過綠界 API，僅更新系統紀錄（已在綠界後台完成退款時勾選）
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" :disabled="refundSubmitting" @click="closeRefundModal">取消</button>
          <button type="button" class="btn btn-danger" :disabled="refundSubmitting" @click="submitRefund">
            {{ refundSubmitting ? '處理中...' : '確認退款' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 升級為正式訂閱 Modal -->
    <div v-if="showUpgradeModal" class="modal-overlay" @click.self="showUpgradeModal = false">
      <div class="extend-modal-content">
        <div class="modal-header">
          <h2>升級為正式訂閱</h2>
          <button class="btn-close" @click="showUpgradeModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="upgrade-notice">
            <i class="ri-information-line"></i>
            與客戶自助升級相同：試用會保留至綠界付款成功，或您稍後確認人工收款後才結束。若選綠界，將另開分頁前往付款頁。
          </div>
          <div class="form-group">
            <label class="form-label">方案 <span class="required">*</span></label>
            <select v-model="upgradeForm.planId" class="form-control" :disabled="loadingPlans">
              <option value="">{{ loadingPlans ? '載入中...' : '請選擇方案' }}</option>
              <option v-for="plan in availablePlans" :key="plan.id" :value="plan.id">
                {{ plan.plan_name || plan.planName }}
              </option>
            </select>
            <span v-if="upgradeFormErrors.planId" class="error-text">{{ upgradeFormErrors.planId }}</span>
          </div>
          <div class="form-group">
            <label class="form-label">計費週期 <span class="required">*</span></label>
            <select v-model="upgradeForm.billingCycle" class="form-control">
              <option value="monthly">月繳（30 天）</option>
              <option value="yearly">年繳（365 天）</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">付款方式 <span class="required">*</span></label>
            <select v-model="upgradeForm.paymentType" class="form-control">
              <option value="manual">人工收款</option>
              <option value="ecpay">綠界扣款</option>
            </select>
            <span v-if="upgradeFormErrors.paymentType" class="error-text">{{ upgradeFormErrors.paymentType }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showUpgradeModal = false" :disabled="upgrading">取消</button>
          <button class="btn btn-success" @click="handleUpgradeSubscription" :disabled="upgrading">
            <span v-if="upgrading">處理中...</span>
            <span v-else><i class="ri-vip-crown-line"></i> 建立升級訂單</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 建立新訂閱 Modal -->
    <div v-if="showCreateSubscriptionModal" class="modal-overlay" @click.self="showCreateSubscriptionModal = false">
      <div class="extend-modal-content">
        <div class="modal-header">
          <h2>建立新訂閱</h2>
          <button class="btn-close" @click="showCreateSubscriptionModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">
              方案 <span class="required">*</span>
            </label>
            <select
              v-model="createSubscriptionForm.planId"
              class="form-control"
              :disabled="loadingPlans"
              required
            >
              <option value="">請選擇方案</option>
              <option
                v-for="plan in availablePlans"
                :key="plan.id"
                :value="plan.id"
              >
                {{ plan.planName || plan.plan_name || '未命名方案' }} ({{ plan.planCode || plan.plan_code || plan.id }})
              </option>
            </select>
            <small class="form-text">選擇要為客戶建立的方案</small>
          </div>

          <div class="form-group">
            <label class="form-label">
              付款方式 <span class="required">*</span>
            </label>
            <select
              v-model="createSubscriptionForm.paymentType"
              class="form-control"
              required
            >
              <option value="">請選擇付款方式</option>
              <option value="manual">人工收款</option>
              <option value="ecpay">綠界扣款</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">
              計費週期
            </label>
            <select
              v-model="createSubscriptionForm.billingCycle"
              class="form-control"
              @change="handleBillingCycleChange"
            >
              <option value="monthly">月繳</option>
              <option value="yearly">年繳</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">
              開始日期
            </label>
            <input
              v-model="createSubscriptionForm.startDate"
              type="date"
              class="form-control"
            />
            <small class="form-text">預設為今天</small>
          </div>

          <div class="form-group">
            <label class="form-label">
              訂閱天數
            </label>
            <input
              v-model.number="createSubscriptionForm.durationDays"
              type="number"
              class="form-control"
              min="1"
              placeholder="30"
            />
            <small class="form-text">
              {{ createSubscriptionForm.billingCycle === 'yearly' ? '年繳預設為 365 天' : '月繳預設為 30 天' }}
            </small>
          </div>

          <div v-if="createSubscriptionFormErrors.planId" class="error-message">
            {{ createSubscriptionFormErrors.planId }}
          </div>
          <div v-if="createSubscriptionFormErrors.paymentType" class="error-message">
            {{ createSubscriptionFormErrors.paymentType }}
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showCreateSubscriptionModal = false" :disabled="creatingSubscription">
            取消
          </button>
          <button class="btn btn-primary" @click="handleCreateSubscription" :disabled="creatingSubscription">
            <span v-if="creatingSubscription" class="spinner-small"></span>
            <span v-else>確認建立</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 延長訂閱 Modal -->
    <div v-if="showExtendModal" class="modal-overlay" @click.self="showExtendModal = false">
      <div class="extend-modal-content">
        <div class="modal-header">
          <h2>延長訂閱</h2>
          <button class="btn-close" @click="showExtendModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">
              收款日期 <span class="required">*</span>
            </label>
            <input
              v-model="extendForm.paymentDate"
              type="date"
              class="form-control"
              :max="getTodayDate()"
              required
            />
            <small class="form-text">請選擇收款日期</small>
          </div>

          <div class="form-group">
            <label class="form-label">
              收款金額
            </label>
            <input
              v-model.number="extendForm.amount"
              type="number"
              class="form-control"
              placeholder="未提供則自動計算"
              min="0"
              step="0.01"
            />
            <small class="form-text">可選，未提供則系統自動計算</small>
          </div>

          <div class="form-group">
            <label class="form-label">
              備註
            </label>
            <textarea
              v-model="extendForm.notes"
              class="form-control"
              rows="3"
              placeholder="例如：銀行轉帳、現金收款等"
            ></textarea>
          </div>

          <div v-if="extendFormErrors.paymentDate" class="error-message">
            {{ extendFormErrors.paymentDate }}
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showExtendModal = false" :disabled="extending">
            取消
          </button>
          <button class="btn btn-primary" @click="handleExtendSubscription" :disabled="extending">
            <span v-if="extending" class="spinner-small"></span>
            <span v-else>確認延長</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 確認人工收款（待開通升級訂閱）Modal -->
    <div v-if="showConfirmPendingModal" class="modal-overlay" @click.self="showConfirmPendingModal = false">
      <div class="extend-modal-content">
        <div class="modal-header">
          <h2>確認升級收款</h2>
          <button class="btn-close" type="button" @click="showConfirmPendingModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <p v-if="pendingManualUpgrade" class="form-text" style="margin-bottom: 16px;">
            訂閱編號：{{ pendingManualUpgrade.id }}／方案：{{ pendingManualUpgrade.plan_name || '-' }}
          </p>
          <div class="form-group">
            <label class="form-label">收款日期 <span class="required">*</span></label>
            <input
              v-model="confirmPendingForm.paymentDate"
              type="date"
              class="form-control"
              :max="getTodayDate()"
            />
            <span v-if="confirmPendingFormErrors.paymentDate" class="error-text">{{ confirmPendingFormErrors.paymentDate }}</span>
          </div>
          <div class="form-group">
            <label class="form-label">收款金額</label>
            <input
              v-model.number="confirmPendingForm.amount"
              type="number"
              class="form-control"
              min="0"
              step="0.01"
              placeholder="未填則依方案月費／年費自動帶入"
            />
          </div>
          <div class="form-group">
            <label class="form-label">備註</label>
            <textarea
              v-model="confirmPendingForm.notes"
              class="form-control"
              rows="3"
              placeholder="例如：轉帳後五碼、現金收款"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showConfirmPendingModal = false" :disabled="confirmingPending">
            取消
          </button>
          <button type="button" class="btn btn-warning" @click="handleConfirmPendingSubscription" :disabled="confirmingPending">
            <span v-if="confirmingPending" class="spinner-small"></span>
            <span v-else>確認已收款並開通</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 後台代客發起綠界升級：隱藏表單 -->
    <form
      ref="upgradeEcpayForm"
      :action="upgradeEcpayPaymentUrl"
      method="POST"
      target="_blank"
      style="display: none"
    >
      <input
        v-for="(value, key) in upgradeEcpayOrderData"
        :key="key"
        type="hidden"
        :name="key"
        :value="value"
      />
    </form>
  </div>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { subscriptionClientsService } from '@/api/modules/subscription-clients.js';
import { planService } from '@/api/modules/plans.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';

export default {
  name: 'DetailModal',
  props: {
    clientId: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const clientData = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const showExtendModal = ref(false);
    const extending = ref(false);
    const extendForm = reactive({
      paymentDate: '',
      amount: null,
      notes: ''
    });
    const extendFormErrors = reactive({
      paymentDate: ''
    });

    const showCreateSubscriptionModal = ref(false);
    const creatingSubscription = ref(false);
    const loadingPlans = ref(false);
    const availablePlans = ref([]);
    const createSubscriptionForm = reactive({
      planId: '',
      paymentType: 'manual',
      billingCycle: 'monthly',
      startDate: '',
      durationDays: 30
    });
    const createSubscriptionFormErrors = reactive({
      planId: '',
      paymentType: ''
    });

    const loadData = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await subscriptionClientsService.getClientSubscriptionDetails(props.clientId);

        if (response && response.success && response.data) {
          clientData.value = response.data;
        } else {
          error.value = response && response.message || '獲取客戶詳細資訊失敗';
        }
      } catch (err) {
        console.error('獲取客戶詳細資訊失敗:', err);
        error.value = err.message || '獲取客戶詳細資訊失敗';
        await showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleClose = () => {
      emit('close');
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('zh-TW', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    };

    const getSubscriptionStatusClass = (status) => {
      const statusMap = {
        'trial': 'badge badge-info',
        'active': 'badge badge-success',
        'grace_period': 'badge badge-warning',
        'expired': 'badge badge-danger',
        'no_subscription': 'badge badge-secondary'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getSubscriptionStatusText = (status) => {
      const statusMap = {
        'trial': '試用中',
        'active': '啟用中',
        'grace_period': '寬限期',
        'expired': '已過期',
        'no_subscription': '無訂閱'
      };
      return statusMap[status] || status || '未知';
    };

    const getSubscriptionRecordStatusClass = (status) => {
      const statusMap = {
        'active': 'badge badge-success',
        'pending': 'badge badge-warning',
        'expired': 'badge badge-danger',
        'cancelled': 'badge badge-secondary'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getSubscriptionRecordStatusText = (status) => {
      const statusMap = {
        'active': '啟用中',
        'pending': '待開通／待付款',
        'expired': '已過期',
        'cancelled': '已取消'
      };
      return statusMap[status] || status || '未知';
    };

    /** 訂閱歷史 API 可能為 snake_case 或 camelCase */
    const getSubscriptionMerchantTradeNo = (sub) => {
      if (!sub || typeof sub !== 'object') return '';
      return sub.ecpay_merchant_trade_no || sub.ecpayMerchantTradeNo || '';
    };

    const getPaymentStatusClass = (status) => {
      const statusMap = {
        'completed': 'badge badge-success',
        'failed': 'badge badge-danger',
        'refunded': 'badge badge-warning',
        'cancel_renewal': 'badge badge-info'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getPaymentStatusText = (status) => {
      const statusMap = {
        'completed': '成功',
        'failed': '失敗',
        'refunded': '已退款',
        'cancel_renewal': '取消續約'
      };
      return statusMap[status] || status || '未知';
    };

    const getPaymentRecordRowClass = (status) => {
      if (status === 'failed') {
        return 'payment-failed';
      }
      if (status === 'cancel_renewal') {
        return 'payment-cancel-renewal';
      }
      if (status === 'refunded') {
        return 'payment-refunded';
      }
      return '';
    };

    const extractFailureReason = (ecpayResponse) => {
      if (!ecpayResponse) return '未知原因';
      try {
        const response = typeof ecpayResponse === 'string' ? JSON.parse(ecpayResponse) : ecpayResponse;
        return response.RtnMsg || response.message || '未知原因';
      } catch {
        return '未知原因';
      }
    };

    /** 畫面上顯示的訂閱列：優先活躍，否則為可延長目標（通常為最新 expired） */
    const subscriptionForDisplay = computed(() => {
      const d = clientData.value;
      if (!d) return null;
      return d.activeSubscription || d.extendableSubscription || null;
    });

    /** 與 POST /client-subscriptions/:id/extend 一致：有 extendableSubscription（或由後端填好）即可延長 */
    const canExtendSubscription = computed(() => {
      const d = clientData.value;
      if (!d) return false;
      if (d.extendableSubscription != null) return true;
      return d.activeSubscription != null;
    });

    const isOnTrial = computed(() => {
      return clientData.value?.activeSubscription?.is_trial === true;
    });

    const pendingManualUpgrade = computed(() => {
      const list = clientData.value?.subscriptionHistory || [];
      return list.find(
        (s) => s.status === 'pending' && !s.is_trial && s.payment_type === 'manual'
      ) || null;
    });

    const pendingEcpayUpgrade = computed(() => {
      const list = clientData.value?.subscriptionHistory || [];
      return list.find(
        (s) => s.status === 'pending' && !s.is_trial && s.payment_type === 'ecpay'
      ) || null;
    });

    const canCancelSubscriptionRenewal = computed(() => {
      const sub = clientData.value?.activeSubscription;
      if (!sub) return false;
      if (sub.is_trial) return false;
      return sub.auto_renew !== false;
    });

    const cancellingRenewal = ref(false);

    const handleCancelSubscriptionRenewal = async () => {
      const ok = await showConfirm(
        '取消自動續約',
        '確定要為此客戶取消訂閱自動續約嗎？目前週期可繼續使用至到期日，到期後不再續扣。'
      );
      if (!ok) return;
      cancellingRenewal.value = true;
      try {
        const response = await subscriptionClientsService.cancelSubscriptionRenewal(props.clientId);
        if (response && response.success) {
          await showSuccess('已更新', response.message || '已取消自動續約');
          await loadData();
        } else {
          await showError('取消失敗', response?.message || '取消失敗');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '取消失敗';
        await showError('取消失敗', msg);
      } finally {
        cancellingRenewal.value = false;
      }
    };

    const showRefundModal = ref(false);
    const refundTargetRecord = ref(null);
    const refundSubmitting = ref(false);
    const refundForm = reactive({
      operatorNote: '',
      refundWithoutEcpay: false,
    });

    const closeRefundModal = () => {
      showRefundModal.value = false;
      refundTargetRecord.value = null;
      refundForm.operatorNote = '';
      refundForm.refundWithoutEcpay = false;
    };

    /** 全額退款：開啟 Modal */
    function handleRefundClick(record) {
      if (!record) return;
      refundTargetRecord.value = record;
      refundForm.operatorNote = '';
      refundForm.refundWithoutEcpay = false;
      showRefundModal.value = true;
    }

    const submitRefund = async () => {
      const rec = refundTargetRecord.value;
      if (!rec?.id) return;
      const ok = await showConfirm(
        '確認全額退款',
        '確定執行退款？訂閱到期日將回滾至該筆扣款前，且此操作通常無法還原。'
      );
      if (!ok) return;
      refundSubmitting.value = true;
      try {
        const response = await subscriptionClientsService.refundSubscriptionPayment(props.clientId, rec.id, {
          operatorNote: refundForm.operatorNote?.trim() || undefined,
          refundWithoutEcpay: Boolean(refundForm.refundWithoutEcpay),
        });
        if (response && response.success) {
          const d = response.data || {};
          const lines = [response.message || '退款處理成功'];
          if (d.invoiceAllowance && d.invoiceAllowance.success === false && !d.invoiceAllowance.skipped) {
            lines.push(`發票折讓未成功：${d.invoiceAllowance.error || '請查綠界後台或 invoice_response'}`);
          }
          if (d.ecpayPeriodCancel && d.ecpayPeriodCancel.success === false) {
            lines.push(`綠界定期定額未終止：${d.ecpayPeriodCancel.message || '請至綠界後台確認'}`);
          }
          await showSuccess('已完成', lines.join('\n'));
          closeRefundModal();
          await loadData();
        } else {
          await showError('失敗', response?.message || '退款失敗');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '退款失敗';
        await showError('退款失敗', msg);
      } finally {
        refundSubmitting.value = false;
      }
    };

    // 升級為正式訂閱
    const showUpgradeModal = ref(false);
    const upgrading = ref(false);
    const upgradeForm = reactive({
      planId: '',
      billingCycle: 'monthly',
      paymentType: 'manual',
    });
    const upgradeFormErrors = reactive({ planId: '', paymentType: '' });

    const upgradeEcpayForm = ref(null);
    const upgradeEcpayPaymentUrl = ref('');
    const upgradeEcpayOrderData = reactive({});

    const showConfirmPendingModal = ref(false);
    const confirmingPending = ref(false);
    const confirmPendingForm = reactive({
      paymentDate: '',
      amount: null,
      notes: ''
    });
    const confirmPendingFormErrors = reactive({ paymentDate: '' });

    watch(() => showUpgradeModal.value, (newVal) => {
      if (newVal) {
        fetchAvailablePlans();
        upgradeForm.planId = clientData.value?.client?.plan_id || '';
        upgradeForm.billingCycle = 'monthly';
        upgradeForm.paymentType = 'manual';
        upgradeFormErrors.planId = '';
        upgradeFormErrors.paymentType = '';
      }
    });

    const handleUpgradeSubscription = async () => {
      upgradeFormErrors.planId = '';
      upgradeFormErrors.paymentType = '';
      if (!upgradeForm.planId) { upgradeFormErrors.planId = '請選擇方案'; return; }
      if (!upgradeForm.paymentType) { upgradeFormErrors.paymentType = '請選擇付款方式'; return; }

      if (upgradeForm.paymentType === 'ecpay') {
        const ok = await showConfirm(
          '發起綠界升級',
          '將另開分頁前往綠界付款。試用會保留至付款成功；未完成付款時客戶仍可繼續試用。確定繼續嗎？'
        );
        if (!ok) return;
      }

      upgrading.value = true;
      try {
        const response = await subscriptionClientsService.upgradeSubscription(props.clientId, {
          planId: upgradeForm.planId,
          paymentType: upgradeForm.paymentType,
          billingCycle: upgradeForm.billingCycle,
        });

        if (response && response.success && response.data) {
          const d = response.data;
          if (d.mode === 'ecpay' && d.paymentUrl && d.orderData) {
            upgradeEcpayPaymentUrl.value = d.paymentUrl;
            Object.keys(upgradeEcpayOrderData).forEach((k) => delete upgradeEcpayOrderData[k]);
            Object.assign(upgradeEcpayOrderData, d.orderData);
            showUpgradeModal.value = false;
            await showSuccess('已建立升級訂單', '即將開啟綠界付款分頁；付款成功後試用將自動結束。');
            await loadData();
            setTimeout(() => upgradeEcpayForm.value?.submit(), 300);
          } else if (d.mode === 'manual') {
            await showSuccess(
              '已建立待收款訂閱',
              '客戶付款後請點「確認升級收款（人工）」完成開通；在此之前試用仍有效。'
            );
            showUpgradeModal.value = false;
            await loadData();
          } else {
            await showSuccess('處理完成', response.message || '已更新');
            showUpgradeModal.value = false;
            await loadData();
          }
        } else {
          await showError('升級失敗', response?.message || '升級失敗，請稍後再試');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.error?.message || err.message;
        await showError('升級失敗', msg || '升級失敗');
      } finally {
        upgrading.value = false;
      }
    };

    const openConfirmPendingModal = () => {
      if (!pendingManualUpgrade.value) return;
      confirmPendingForm.paymentDate = getTodayDate();
      confirmPendingForm.amount = null;
      confirmPendingForm.notes = '';
      confirmPendingFormErrors.paymentDate = '';
      showConfirmPendingModal.value = true;
    };

    const handleConfirmPendingSubscription = async () => {
      confirmPendingFormErrors.paymentDate = '';
      if (!confirmPendingForm.paymentDate) {
        confirmPendingFormErrors.paymentDate = '請選擇收款日期';
        return;
      }
      const sub = pendingManualUpgrade.value;
      if (!sub?.id) {
        await showError('錯誤', '找不到待確認的訂閱');
        return;
      }

      confirmingPending.value = true;
      try {
        const payload = {
          subscriptionId: sub.id,
          paymentDate: confirmPendingForm.paymentDate,
          notes: confirmPendingForm.notes || undefined
        };
        if (confirmPendingForm.amount !== null && confirmPendingForm.amount !== '') {
          payload.amount = confirmPendingForm.amount;
        }
        const response = await subscriptionClientsService.confirmPendingSubscription(props.clientId, payload);
        if (response && response.success) {
          await showSuccess('已開通', '已確認收款，正式訂閱已啟用。');
          showConfirmPendingModal.value = false;
          await loadData();
        } else {
          await showError('確認失敗', response?.message || '請稍後再試');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '確認失敗';
        await showError('確認失敗', msg);
      } finally {
        confirmingPending.value = false;
      }
    };

    const handleExtendButtonClick = () => {
      if (!canExtendSubscription.value) {
        showError(
          '無法延長訂閱',
          '此客戶沒有可延長的訂閱紀錄，無法延長。請先為客戶建立新的訂閱。'
        );
        return;
      }
      showExtendModal.value = true;
    };

    const getTodayDate = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    /** 將 input date 的 YYYY-MM-DD 轉為本地日期的 00:00（避免只用 new Date(str) 被當成 UTC 午夜而在 +8 誤判為「晚於今天」） */
    const parseLocalDateFromYmd = (ymd) => {
      if (!ymd || typeof ymd !== 'string') return null;
      const parts = ymd.split('-').map((x) => parseInt(x, 10));
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
      const [y, m, d] = parts;
      return new Date(y, m - 1, d);
    };

    const validateExtendForm = () => {
      extendFormErrors.paymentDate = '';
      
      if (!extendForm.paymentDate) {
        extendFormErrors.paymentDate = '請選擇收款日期';
        return false;
      }

      const paymentDay = parseLocalDateFromYmd(extendForm.paymentDate);
      if (!paymentDay) {
        extendFormErrors.paymentDate = '收款日期格式不正確';
        return false;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (paymentDay > today) {
        extendFormErrors.paymentDate = '收款日期不能超過今天';
        return false;
      }

      if (extendForm.amount !== null && extendForm.amount < 0) {
        extendFormErrors.amount = '收款金額不能為負數';
        return false;
      }

      return true;
    };

    const resetExtendForm = () => {
      extendForm.paymentDate = '';
      extendForm.amount = null;
      extendForm.notes = '';
      extendFormErrors.paymentDate = '';
    };

    const fetchAvailablePlans = async () => {
      loadingPlans.value = true;
      try {
        const response = await planService.getPlans({ status: 'active', pageSize: 100 });
        console.log('[Detail] 方案列表 API 回應:', response);
        if (response && response.success && response.data) {
          const plans = response.data.items || response.data || [];
          console.log('[Detail] 解析後的方案列表:', plans);
          // 過濾掉試用版方案（通常不需要為客戶建立試用訂閱）
          availablePlans.value = plans.filter(plan => !plan.is_trial && !plan.isTrial);
          console.log('[Detail] 過濾後的方案列表:', availablePlans.value);
        } else {
          console.warn('[Detail] 方案列表 API 回應格式異常:', response);
          availablePlans.value = [];
        }
      } catch (err) {
        console.error('獲取方案列表失敗:', err);
        await showError('錯誤', '獲取方案列表失敗：' + (err.message || '未知錯誤'));
        availablePlans.value = [];
      } finally {
        loadingPlans.value = false;
      }
    };

    const validateCreateSubscriptionForm = () => {
      createSubscriptionFormErrors.planId = '';
      createSubscriptionFormErrors.paymentType = '';
      
      if (!createSubscriptionForm.planId) {
        createSubscriptionFormErrors.planId = '請選擇方案';
        return false;
      }

      if (!createSubscriptionForm.paymentType) {
        createSubscriptionFormErrors.paymentType = '請選擇付款方式';
        return false;
      }

      if (createSubscriptionForm.durationDays && createSubscriptionForm.durationDays < 1) {
        createSubscriptionFormErrors.durationDays = '訂閱天數必須大於 0';
        return false;
      }

      return true;
    };

    const resetCreateSubscriptionForm = () => {
      createSubscriptionForm.planId = '';
      createSubscriptionForm.paymentType = 'manual';
      createSubscriptionForm.billingCycle = 'monthly';
      createSubscriptionForm.startDate = '';
      createSubscriptionForm.durationDays = 30;
      createSubscriptionFormErrors.planId = '';
      createSubscriptionFormErrors.paymentType = '';
    };

    const handleCreateSubscription = async () => {
      if (!validateCreateSubscriptionForm()) {
        return;
      }

      creatingSubscription.value = true;

      try {
        const data = {
          planId: createSubscriptionForm.planId,
          paymentType: createSubscriptionForm.paymentType,
          billingCycle: createSubscriptionForm.billingCycle
        };

        if (createSubscriptionForm.startDate) {
          data.startDate = createSubscriptionForm.startDate;
        }

        if (createSubscriptionForm.durationDays) {
          data.durationDays = createSubscriptionForm.durationDays;
        }

        const response = await subscriptionClientsService.createSubscription(props.clientId, data);

        if (response && response.success) {
          await showSuccess(
            '建立新訂閱成功',
            '客戶的新訂閱已建立，現在可以使用延長訂閱功能'
          );
          
          showCreateSubscriptionModal.value = false;
          resetCreateSubscriptionForm();
          
          // 重新載入資料
          await loadData();
        } else {
          const errorMsg = response?.message || '建立新訂閱失敗';
          await showError('建立新訂閱失敗', errorMsg);
        }
      } catch (err) {
        console.error('建立新訂閱失敗:', err);
        const errorMsg = err.response?.data?.message || err.message || '建立新訂閱失敗';
        await showError('建立新訂閱失敗', errorMsg);
      } finally {
        creatingSubscription.value = false;
      }
    };

    const handleBillingCycleChange = () => {
      // 當計費週期改變時，自動調整天數
      if (createSubscriptionForm.billingCycle === 'yearly') {
        createSubscriptionForm.durationDays = 365;
      } else {
        createSubscriptionForm.durationDays = 30;
      }
    };

    watch(() => showCreateSubscriptionModal.value, (newVal) => {
      if (newVal) {
        // 當 Modal 打開時，載入方案列表並設定預設值
        fetchAvailablePlans();
        const today = getTodayDate();
        createSubscriptionForm.startDate = today;
        // 如果客戶有方案，預設選擇該方案
        if (clientData.value?.client?.plan_id) {
          createSubscriptionForm.planId = clientData.value.client.plan_id;
        }
        // 根據計費週期設定預設天數
        if (createSubscriptionForm.billingCycle === 'yearly') {
          createSubscriptionForm.durationDays = 365;
        } else {
          createSubscriptionForm.durationDays = 30;
        }
      }
    });

    const handleExtendSubscription = async () => {
      if (!validateExtendForm()) {
        return;
      }

      extending.value = true;

      try {
        const data = {
          paymentDate: extendForm.paymentDate
        };

        if (extendForm.amount !== null && extendForm.amount > 0) {
          data.amount = extendForm.amount;
        }

        if (extendForm.notes) {
          data.notes = extendForm.notes;
        }

        const response = await subscriptionClientsService.extendSubscription(props.clientId, data);

        if (response && response.success) {
          await showSuccess(
            '延長訂閱成功',
            `訂閱已延長 ${response.data?.extendedDays || 30} 天，新到期日期：${formatDate(response.data?.newEndDate)}`
          );
          
          showExtendModal.value = false;
          resetExtendForm();
          
          // 重新載入資料
          await loadData();
        } else {
          const errorMsg = response?.message || '延長訂閱失敗';
          await showError('延長訂閱失敗', errorMsg);
        }
      } catch (err) {
        console.error('延長訂閱失敗:', err);
        const errorMsg = err.response?.data?.message || err.message || '延長訂閱失敗';
        await showError('延長訂閱失敗', errorMsg);
      } finally {
        extending.value = false;
      }
    };

    watch(() => props.clientId, () => {
      if (props.clientId) {
        loadData();
      }
    }, { immediate: true });

    onMounted(() => {
      if (props.clientId) {
        loadData();
      }
    });

    return {
      clientData,
      subscriptionForDisplay,
      loading,
      error,
      showExtendModal,
      extending,
      extendForm,
      extendFormErrors,
      loadData,
      handleClose,
      formatDate,
      formatCurrency,
      getSubscriptionStatusClass,
      getSubscriptionStatusText,
      getSubscriptionRecordStatusClass,
      getSubscriptionRecordStatusText,
      getSubscriptionMerchantTradeNo,
      getPaymentStatusClass,
      getPaymentStatusText,
      getPaymentRecordRowClass,
      extractFailureReason,
      canExtendSubscription,
      canCancelSubscriptionRenewal,
      cancellingRenewal,
      handleCancelSubscriptionRenewal,
      showRefundModal,
      refundTargetRecord,
      refundSubmitting,
      refundForm,
      closeRefundModal,
      handleRefundClick,
      submitRefund,
      isOnTrial,
      pendingManualUpgrade,
      pendingEcpayUpgrade,
      showUpgradeModal,
      upgrading,
      upgradeForm,
      upgradeFormErrors,
      upgradeEcpayForm,
      upgradeEcpayPaymentUrl,
      upgradeEcpayOrderData,
      showConfirmPendingModal,
      confirmingPending,
      confirmPendingForm,
      confirmPendingFormErrors,
      handleUpgradeSubscription,
      openConfirmPendingModal,
      handleConfirmPendingSubscription,
      handleExtendButtonClick,
      showCreateSubscriptionModal,
      creatingSubscription,
      loadingPlans,
      availablePlans,
      createSubscriptionForm,
      createSubscriptionFormErrors,
      handleBillingCycleChange,
      handleCreateSubscription,
      getTodayDate,
      handleExtendSubscription
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* 高於 sidebar(200)、浮動子選單(1001)、操作說明(10000) */
  z-index: 11000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  color: #333;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
}

.info-section {
  margin-bottom: 32px;
}

.info-section:last-child {
  margin-bottom: 0;
}

.info-section h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.action-buttons-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.info-note-trial {
  background: #fffbeb;
  border-color: #fcd34d;
  color: #92400e;
}

.info-note-pending {
  background: #e8f4fd;
  border: 1px solid #90caf9;
  color: #1565c0;
}

.upgrade-notice {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 0.875rem;
  color: #92400e;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.upgrade-notice i { flex-shrink: 0; margin-top: 1px; }

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.section-header h3 {
  margin: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.action-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.info-note {
  margin-top: 12px;
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #856404;
  font-size: 14px;
}

.info-note i {
  font-size: 18px;
  flex-shrink: 0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  gap: 8px;
}

.info-label {
  font-weight: 500;
  color: #666;
}

.info-value {
  color: #333;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  padding: 20px;
  border-radius: 8px;
  border: 2px solid;
}

.stat-card.success {
  background: #e8f5e9;
  border-color: #4caf50;
}

.stat-card.failed {
  background: #ffebee;
  border-color: #f44336;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 4px;
}

.stat-amount {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
}

.stat-date {
  font-size: 12px;
  color: #666;
}

.table-scroll-hint {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px 0;
}

/* 訂閱歷史欄位多時需橫向捲動，否則右側欄位會被裁切看不到 */
.detail-table-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.detail-table--subscription-history {
  min-width: 920px;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}

.detail-table th,
.detail-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.detail-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
}

.detail-table tr.payment-failed {
  background: #ffebee;
}

.detail-table tr.payment-cancel-renewal {
  background: #e3f2fd;
}

.detail-table tr.payment-refunded {
  background: #fff8e1;
}

.detail-table th.col-actions,
.detail-table td.col-actions {
  white-space: nowrap;
  width: 1%;
}

.detail-table tr:hover {
  background: #f5f5f5;
}

.merchant-trade-no {
  font-size: 12px;
  word-break: break-all;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1976d2;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.text-danger {
  color: #dc3545;
}

.text-muted {
  color: #999;
  font-style: italic;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-secondary {
  background: #757575;
  color: white;
}

.btn-secondary:hover {
  background: #616161;
}

.btn-primary {
  background: #1976d2;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #1565c0;
}

.btn-primary:disabled {
  background: #90caf9;
  cursor: not-allowed;
}

/* 延長訂閱 Modal */
.extend-modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.form-label .required {
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.error-message {
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
}

.spinner-small {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
</style>
